import { useState, useCallback, useEffect } from 'react';
import { difficultyService, type DifficultyInfo, type AnswerOptionInfo } from '@/services';
import { DIFFICULTY_LEVELS_ARRAY, ANSWER_OPTIONS_ARRAY } from '@/constants/assessment';

export const useDifficulty = () => {
  const [difficulties, setDifficulties] = useState<DifficultyInfo[]>([]);
  const [answerOptions, setAnswerOptions] = useState<AnswerOptionInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDifficulties = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await difficultyService.getDifficulties();
      
      if (response.success && response.data) {
        setDifficulties(response.data.difficulties || []);
      } else {
        // Fallback to client-side data
        setDifficulties(difficultyService.getDifficultyLevels());
      }
    } catch (err) {
      // Fallback to client-side data on error
      setDifficulties(difficultyService.getDifficultyLevels());
      console.warn('Failed to fetch difficulties from API, using fallback data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAnswerOptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await difficultyService.getAnswerOptions();
      
      if (response.success && response.data) {
        setAnswerOptions(response.data.options || []);
      } else {
        // Fallback to client-side data
        setAnswerOptions(difficultyService.getAnswerOptionsLocal());
      }
    } catch (err) {
      // Fallback to client-side data on error
      setAnswerOptions(difficultyService.getAnswerOptionsLocal());
      console.warn('Failed to fetch answer options from API, using fallback data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllEnums = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await difficultyService.getAllEnums();
      
      if (response.success && response.data) {
        setDifficulties(response.data.difficulties || []);
        setAnswerOptions(response.data.answerOptions || []);
      } else {
        // Fallback to client-side data
        setDifficulties(difficultyService.getDifficultyLevels());
        setAnswerOptions(difficultyService.getAnswerOptionsLocal());
      }
    } catch (err) {
      // Fallback to client-side data on error
      setDifficulties(difficultyService.getDifficultyLevels());
      setAnswerOptions(difficultyService.getAnswerOptionsLocal());
      console.warn('Failed to fetch enums from API, using fallback data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize with fallback data
  useEffect(() => {
    if (difficulties.length === 0) {
      setDifficulties(difficultyService.getDifficultyLevels());
    }
    if (answerOptions.length === 0) {
      setAnswerOptions(difficultyService.getAnswerOptionsLocal());
    }
  }, [difficulties.length, answerOptions.length]);

  // Utility functions
  const getDifficultyColor = useCallback((difficulty: string) => {
    return difficultyService.getDifficultyColor(difficulty as any);
  }, []);

  const getDifficultyOrder = useCallback((difficulty: string) => {
    return difficultyService.getDifficultyOrder(difficulty as any);
  }, []);

  const isValidDifficulty = useCallback((difficulty: string) => {
    return difficultyService.isValidDifficulty(difficulty);
  }, []);

  const isValidAnswerOption = useCallback((option: string) => {
    return difficultyService.isValidAnswerOption(option);
  }, []);

  // Get arrays for dropdowns
  const difficultyLevels = DIFFICULTY_LEVELS_ARRAY;
  const answerOptionValues = ANSWER_OPTIONS_ARRAY;

  return {
    difficulties,
    answerOptions,
    difficultyLevels,
    answerOptionValues,
    loading,
    error,
    fetchDifficulties,
    fetchAnswerOptions,
    fetchAllEnums,
    getDifficultyColor,
    getDifficultyOrder,
    isValidDifficulty,
    isValidAnswerOption,
  };
};